import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  LifeBuoy,
  DollarSign,
  AlertTriangle,
  FileText,
  Target,
  Info,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';

interface DecisionOutputsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  /**
   * A snapshot past Draft cannot be written to (`Update` refuses anything else and autosave does
   * not fire), so the fields are read-only rather than accepting typing that reaches nothing —
   * the same defect class as the "Save Decisions" button CO-433 removed.
   */
  readOnly?: boolean;
  onDecisionsUpdate: (decisions: DecisionState) => void;
}

interface DecisionState {
  growthFocus: string;
  supportNeeded: string;
  compensationInput: string;
  riskFlag: string;
  riskDetails: string;
}

const DecisionOutputsPanel = ({
  employee,
  reviewData,
  readOnly = false,
  onDecisionsUpdate,
}: DecisionOutputsPanelProps) => {
  const { t } = useTranslation();

  /**
   * CO-432 — seeded from `reviewData.decisions`, the LIVE page state. The page seeds that from the
   * saved snapshot once, at generate time, so this reads back the manager's in-progress edits
   * rather than the last thing the server happened to hold.
   */
  const [decisions, setDecisions] = useState<DecisionState>(() => {
    const saved = (reviewData.decisions ?? {}) as unknown as Partial<DecisionState>;
    return {
      growthFocus: saved.growthFocus || '',
      supportNeeded: saved.supportNeeded || '',
      compensationInput: saved.compensationInput || '',
      riskFlag: saved.riskFlag || 'none',
      riskDetails: saved.riskDetails || '',
    };
  });

  const riskFlagOptions = [
    {
      value: 'none',
      label: t('performanceSnapshot.decisions.riskOptions.none'),
    },
    {
      value: 'performance',
      label: t('performanceSnapshot.decisions.riskOptions.performance'),
    },
    {
      value: 'retention',
      label: t('performanceSnapshot.decisions.riskOptions.retention'),
    },
    {
      value: 'engagement',
      label: t('performanceSnapshot.decisions.riskOptions.engagement'),
    },
    {
      value: 'skill-gap',
      label: t('performanceSnapshot.decisions.riskOptions.skillGap'),
    },
  ];

  /**
   * CO-433 — this used to update LOCAL state only, so everything typed on this tab reached nothing:
   * not page state, not the server, not even the panel's own "Save Decisions" button, which sent
   * whatever the last committed object was. Every keystroke now lifts to the page, which autosaves.
   */
  const handleFieldChange = (field: keyof DecisionState, value: string) => {
    if (readOnly) return;
    const updatedDecisions = {
      ...decisions,
      [field]: value,
    };
    setDecisions(updatedDecisions);
    onDecisionsUpdate(updatedDecisions);
  };

  const isComplete =
    decisions.growthFocus.trim().length > 0 &&
    decisions.supportNeeded.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        {/* CO-433 — the in-panel "Save Decisions" button is GONE. It flipped to a green "Saved"
            state for a call that only touched React state, and it was the ONLY thing that lifted
            these fields off the panel, so a manager who typed and switched tabs lost everything.
            Fields now lift on change and the page autosaves; the status bar is the one indicator. */}
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <CheckCircle size={24} />
            <span>{t('performanceSnapshot.decisions.title')}</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('performanceSnapshot.decisions.subtitle', {
              name: employee.name,
            })}
          </p>
        </div>

        {/* Completion Status */}
        <div className="flex items-center space-x-2 text-sm">
          {isComplete ? (
            <>
              <CheckCircle2 size={16} className="text-green-500" />
              <span className="text-green-600 font-medium">
                {t('performanceSnapshot.decisions.allComplete')}
              </span>
            </>
          ) : (
            <>
              <AlertCircle size={16} className="text-amber-500" />
              <span className="text-amber-600 font-medium">
                {t('performanceSnapshot.decisions.requiredFields')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Growth Focus */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.growthFocus.label')}</span>
              <span className="text-red-500 text-sm">*</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.growthFocus.description')}
            </p>
            <textarea
              value={decisions.growthFocus}
              onChange={(e) => handleFieldChange('growthFocus', e.target.value)}
              placeholder={t('performanceSnapshot.decisions.growthFocus.placeholder')}
              readOnly={readOnly}
              rows={6}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.growthFocus.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Support Needed */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <LifeBuoy size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.supportNeeded.label')}</span>
              <span className="text-red-500 text-sm">*</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.supportNeeded.description')}
            </p>
            <textarea
              value={decisions.supportNeeded}
              onChange={(e) =>
                handleFieldChange('supportNeeded', e.target.value)
              }
              placeholder={t('performanceSnapshot.decisions.supportNeeded.placeholder')}
              readOnly={readOnly}
              rows={6}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.supportNeeded.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Compensation Input */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <DollarSign size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center space-x-2">
              <span>{t('performanceSnapshot.decisions.compensationInput.label')}</span>
              <span className="text-muted-foreground text-sm font-normal">
                ({t('performanceSnapshot.decisions.compensationInput.optional')})
              </span>
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              {t('performanceSnapshot.decisions.compensationInput.description')}
            </p>
            <textarea
              value={decisions.compensationInput}
              onChange={(e) =>
                handleFieldChange('compensationInput', e.target.value)
              }
              placeholder={t('performanceSnapshot.decisions.compensationInput.placeholder')}
              readOnly={readOnly}
              rows={5}
              className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
            />
            <div className="text-xs text-muted-foreground mt-2">
              {decisions.compensationInput.length || 0}{' '}
              {t('performanceSnapshot.decisions.characters')}
            </div>
          </div>
        </div>
      </div>

      {/* Risk Flag */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2">
              {t('performanceSnapshot.decisions.riskFlag.label')}
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              {t('performanceSnapshot.decisions.riskFlag.description')}
            </p>

            <select
              value={decisions.riskFlag}
              onChange={(e) => handleFieldChange('riskFlag', e.target.value)}
              disabled={readOnly}
              className="w-full p-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              {riskFlagOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {decisions.riskFlag !== 'none' && (
              <div className="mt-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t('performanceSnapshot.decisions.riskFlag.detailsLabel')}
                </label>
                <textarea
                  value={decisions.riskDetails}
                  onChange={(e) =>
                    handleFieldChange('riskDetails', e.target.value)
                  }
                  placeholder={t('performanceSnapshot.decisions.riskFlag.detailsPlaceholder')}
                  readOnly={readOnly}
                  rows={5}
                  className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                />
                <div className="text-xs text-muted-foreground mt-2">
                  {decisions.riskDetails.length || 0}{' '}
                  {t('performanceSnapshot.decisions.characters')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
        <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
          <FileText size={18} />
          <span>{t('performanceSnapshot.decisions.summary.title')}</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Target size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.growthFocus.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.growthFocus
                ? `${decisions.growthFocus.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.notSpecified')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <LifeBuoy size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.supportNeeded.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.supportNeeded
                ? `${decisions.supportNeeded.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.notSpecified')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.compensationInput.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {decisions.compensationInput
                ? `${decisions.compensationInput.substring(0, 100)}...`
                : t('performanceSnapshot.decisions.summary.noInput')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle size={16} className="text-purple-500" />
              <span className="text-sm font-medium text-foreground">
                {t('performanceSnapshot.decisions.riskFlag.label')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {riskFlagOptions.find((opt) => opt.value === decisions.riskFlag)
                ?.label || t('performanceSnapshot.decisions.summary.notAssessed')}
            </p>
          </div>
        </div>
      </div>

      {/* CO-433 — where the second Save button and a dead "Export Review" button used to sit. The
          export had no handler at all; a control that does nothing is the same defect class as a
          toast for a write that never happened. What is left is the honest statement of where these
          fields go. */}
      <div className="flex items-start gap-2 bg-card border border-border rounded-lg p-6">
        <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <span className="text-sm text-muted-foreground">
          {readOnly
            ? t('performanceSnapshot.decisions.readOnlyNote')
            : t('performanceSnapshot.decisions.autosaveNote')}
        </span>
      </div>
    </div>
  );
};

export default DecisionOutputsPanel;
